/**
 * Woche & Verlauf.
 *
 * Unterschied zum Entwurf: „Korrigieren“ ändert hier nichts, sondern stellt
 * einen Antrag. Die Zeile behält ihre ursprünglichen Werte und bekommt ein
 * Kennzeichen „in Prüfung“, bis die Verwaltung entschieden hat.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MINUTE_MS, addDays, berlinToTs, breakMs, byDate, dailyTargetMs, dateLabel, dayKey,
  de, dowLabel, fmtDuration, fmtSigned, hhmm, isWeekend, isoWeek, netMs, settledNetMs,
  terminalStatus, weekKeys, weekStart, weekSummary,
  type CorrectionRequest, type DayEntry, type DayKey,
} from '@zeit/domain';
import { serverNow } from '../lib/clock.ts';
import { ApiError, api, read } from '../lib/appwrite.ts';
import { useNow } from '../lib/session.ts';
import type { Session } from '../lib/session.ts';
import { Card, Dialog, ErrorText, Field, Figure, Kicker, Page } from '../ui/index.tsx';

const w = de.week;
const c = de.correction;

export function Week({
  session,
  onToast,
}: {
  session: Session;
  onToast: (text: string, tone?: 'error') => void;
}) {
  const { employee } = session;
  const employeeId = employee.$id!;
  const today = dayKey(serverNow());

  const [offset, setOffset] = useState(0);
  const [entries, setEntries] = useState<DayEntry[] | null>(null);
  const [pending, setPending] = useState<CorrectionRequest[]>([]);
  const [editing, setEditing] = useState<DayKey | null>(null);

  const monday = addDays(weekStart(today), offset * 7);
  const keys = weekKeys(monday);

  const load = useCallback(async () => {
    const [days, corrections] = await Promise.all([
      read.days(employeeId, keys[0], keys[6]),
      read.corrections(employeeId, keys[0], keys[6]),
    ]);
    setEntries(days);
    setPending(corrections.filter((x) => x.status === 'pending'));
  }, [employeeId, keys[0], keys[6]]);

  useEffect(() => {
    void load().catch(() => onToast(de.error.generic, 'error'));
  }, [load, onToast]);

  const byKey = useMemo(() => byDate(entries ?? []), [entries]);
  const pendingDates = useMemo(() => new Set(pending.map((p) => p.date)), [pending]);

  // Läuft ein Tag dieser Woche noch, muss die Ansicht ticken: vorher stand
  // `now` seit dem Laden fest, und die Arbeitszeit des laufenden Tages blieb
  // stehen, bis man die Woche wechselte.
  const running = useMemo(
    () => [...byKey.values()].some((e) => e.clockIn && !e.clockOut && e.status !== 'missing_out'),
    [byKey],
  );
  const now = useNow(running, 30_000);
  const summary = weekSummary(employee, monday, byKey, now);
  const dayTarget = dailyTargetMs(employee.weeklyTargetHours, employee.workdaysPerWeek);

  const navigation = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button
        type="button"
        className="btn btn-secondary"
        style={{ minHeight: 44, minWidth: 56 }}
        aria-label={w.prev}
        onClick={() => setOffset((o) => o - 1)}
      >
        ←
      </button>
      <button
        type="button"
        className="btn btn-secondary"
        style={{ minHeight: 44, minWidth: 56 }}
        aria-label={w.next}
        disabled={offset >= 0}
        onClick={() => setOffset((o) => Math.min(0, o + 1))}
      >
        →
      </button>
      {offset !== 0 && (
        <button type="button" className="btn btn-ghost" onClick={() => setOffset(0)}>
          {w.current}
        </button>
      )}
    </div>
  );

  return (
    <Page
      title={w.title(isoWeek(monday))}
      kicker={`${dateLabel(keys[0])} – ${dateLabel(keys[6])} · ${employee.name}`}
      actions={
        <>
          {navigation}
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
            <Figure label={w.net} value={fmtDuration(summary.netMs)} />
            <Figure label={w.target} value={fmtDuration(summary.targetMs)} />
            <Figure label={w.colDiff} value={fmtSigned(summary.diffMs)} />
          </div>
        </>
      }
    >
      <Card flush>
      <table className="table table-stack">
        <thead>
          <tr>
            <th>{w.colDate}</th>
            <th>{w.colIn}</th>
            <th>{w.colOut}</th>
            <th>{w.colBreak}</th>
            <th>{w.colNet}</th>
            <th>{w.colDiff}</th>
            <th style={{ textAlign: 'right' }}>{w.colAction}</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => (
            <Row
              key={key}
              date={key}
              entry={byKey.get(key) ?? null}
              isToday={key === today}
              isPending={pendingDates.has(key)}
              dayTarget={dayTarget}
              now={now}
              future={key > today}
              onEdit={() => setEditing(key)}
            />
          ))}
        </tbody>
      </table>
      </Card>

      <p className="text-muted" style={{ fontSize: 12, maxWidth: '70ch', margin: 0 }}>
        {w.footnote}
      </p>

      {editing && (
        <CorrectionDialog
          date={editing}
          entry={byKey.get(editing) ?? null}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            onToast(de.toast.correction_requested);
            await load().catch(() => {});
          }}
        />
      )}
    </Page>
  );
}

function Row({
  date,
  entry,
  isToday,
  isPending,
  dayTarget,
  now,
  future,
  onEdit,
}: {
  date: DayKey;
  entry: DayEntry | null;
  isToday: boolean;
  isPending: boolean;
  dayTarget: number;
  now: number;
  future: boolean;
  onEdit: () => void;
}) {
  const weekend = isWeekend(date);
  const net = settledNetMs(entry, now);
  const open = entry?.status === 'missing_out' || (entry?.clockIn && !entry.clockOut);

  // Am Wochenende wird kein Soll angerechnet — ein Minus für jeden Sonntag
  // wäre Unsinn.
  const diff = entry?.clockIn && !weekend ? net - dayTarget : 0;

  const cls = [isToday ? 'row-today' : '', weekend ? 'row-weekend' : ''].filter(Boolean).join(' ');

  const hasDiff = Boolean(entry?.clockIn) && !weekend;

  return (
    <tr className={cls}>
      <td className="cell-day" style={{ whiteSpace: 'nowrap' }}>
        <strong style={{ fontFamily: 'var(--font-heading)' }}>{dowLabel(date)}</strong>{' '}
        <span className="text-muted">{dateLabel(date)}</span>
      </td>
      <td className="num" data-label={w.colIn}>
        {entry?.clockIn ? hhmm(entry.clockIn) : w.empty}
      </td>
      <td className="num" data-label={w.colOut}>
        {entry?.clockOut ? (
          hhmm(entry.clockOut)
        ) : open ? (
          <span className="tag tag-open">{w.open}</span>
        ) : (
          w.empty
        )}
      </td>
      <td className="num" data-label={w.colBreak}>
        {entry ? fmtDuration(breakMs(entry, now)) : w.empty}
      </td>
      <td className="num" style={{ fontWeight: 600 }} data-label={w.colNet}>
        {entry?.clockIn ? fmtDuration(net) : w.empty}
      </td>
      <td className="num text-muted" data-label={w.colDiff} data-empty={hasDiff ? undefined : ''}>
        {hasDiff ? fmtSigned(diff) : ''}
      </td>
      <td className="cell-action" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {entry?.corrected && (
          <span className="tag tag-neutral" style={{ marginRight: 8 }}>
            {w.corrected}
          </span>
        )}
        {isPending ? (
          <span className="tag tag-accent">{w.pending}</span>
        ) : (
          <button
            type="button"
            className="btn btn-secondary"
            style={{ minHeight: 40 }}
            disabled={future}
            onClick={onEdit}
          >
            {w.correct}
          </button>
        )}
      </td>
    </tr>
  );
}

/**
 * Antragsformular.
 *
 * Die Nettozeit wird live mit derselben Funktion gerechnet, die der Server
 * später anwendet — wer den Antrag abschickt, hat das Ergebnis bereits
 * gesehen.
 */
function CorrectionDialog({
  date,
  entry,
  onClose,
  onDone,
}: {
  date: DayKey;
  entry: DayEntry | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const now = serverNow();
  const [from, setFrom] = useState(entry?.clockIn ? hhmm(entry.clockIn) : '09:00');
  const [to, setTo] = useState(entry?.clockOut ? hhmm(entry.clockOut) : '17:00');
  const [pause, setPause] = useState(entry ? Math.round(breakMs(entry, now) / MINUTE_MS) : 30);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const preview = useMemo(() => {
    const inTs = berlinToTs(date, from);
    let outTs = berlinToTs(date, to);
    if (outTs <= inTs) outTs += 24 * 60 * 60 * 1000;
    const breakTotal = Math.max(0, pause) * MINUTE_MS;
    if (breakTotal >= outTs - inTs) return null;
    return netMs({ clockIn: inTs, clockOut: outTs, breaks: [] }, now) - breakTotal;
  }, [date, from, to, pause, now]);

  const submit = async () => {
    if (busy) return;
    if (!reason.trim()) {
      setError(c.reasonRequired);
      return;
    }
    if (preview === null) {
      setError(c.breakTooLong);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.submitCorrection({
        date,
        in: from,
        out: to,
        breakMinutes: Math.max(0, pause),
        reason: reason.trim(),
      });
      onDone();
    } catch (err) {
      setError((err as ApiError).message || de.error.generic);
    } finally {
      setBusy(false);
    }
  };

  const previousStatus = entry ? de.status[terminalStatus(entry)] : de.status.off;

  return (
    <Dialog title={`${dowLabel(date)}, ${dateLabel(date)}`} kicker={c.subtitle} onClose={onClose}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 130px), 1fr))',
          gap: 14,
        }}
      >
        <Field label={c.in} htmlFor="corr-in">
          <input
            id="corr-in"
            className="input"
            type="time"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={{ minHeight: 48, fontSize: 17 }}
          />
        </Field>
        <Field label={c.out} htmlFor="corr-out">
          <input
            id="corr-out"
            className="input"
            type="time"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            style={{ minHeight: 48, fontSize: 17 }}
          />
        </Field>
        <Field label={c.breakMinutes} htmlFor="corr-break">
          <input
            id="corr-break"
            className="input"
            type="number"
            min={0}
            step={5}
            value={pause}
            onChange={(e) => setPause(Number(e.target.value))}
            style={{ minHeight: 48, fontSize: 17 }}
          />
        </Field>
      </div>

      <div style={{ marginTop: 14 }}>
        <Field label={c.reason} htmlFor="corr-reason">
          <textarea
            id="corr-reason"
            className="input"
            value={reason}
            placeholder={c.reasonPlaceholder}
            onChange={(e) => setReason(e.target.value)}
            style={{ minHeight: 70 }}
          />
        </Field>
      </div>

      <div
        style={{
          marginTop: 16,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          borderTop: 'var(--border)',
          paddingTop: 14,
        }}
      >
        <Kicker>
          {c.previous}: {previousStatus}
          {entry?.clockIn ? ` · ${fmtDuration(netMs(entry, now))}` : ''}
        </Kicker>
        <span className="figure-value num" style={{ fontSize: 24 }}>
          {preview === null ? '—' : fmtDuration(preview)}
        </span>
      </div>

      {error && (
        <div style={{ marginTop: 12 }}>
          <ErrorText>{error}</ErrorText>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        <button
          type="button"
          className="btn btn-primary"
          style={{ minHeight: 52, fontSize: 16 }}
          disabled={busy}
          onClick={submit}
        >
          {c.submit}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          style={{ minHeight: 52, fontSize: 16, borderWidth: 2 }}
          onClick={onClose}
        >
          {c.cancel}
        </button>
      </div>
    </Dialog>
  );
}
